import { useNavigate, useParams } from 'react-router-dom'
import { Button, Badge, EmptyState, Select } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import Page from '../../components/layouts/page'
import { useState } from 'react'
import NewIssue from './components/new_issue'
import useTableFilters from '../../hooks/use_table_filters'
import ResolveIssueModal from './components/resolve_issue_modal'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

export default function Issues() {
  const navigate = useNavigate()
  const airlineID = useParams().airlineID
  const cycleID = useParams().cycleID

  const [issue, setIssue] = useState(null)
  const { data: airline } = useQuery([`airlines/${airlineID}`])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryKey = `issues?cycleID=${cycleID}`
  const filteringOptions = [
    {
      label: 'Type',
      key: 'type',
      type: 'checkboxes',
      options: [
        {
          label: 'Metadata',
          key: 'metadata',
          value: 'metadata',
        },
        {
          label: 'Images',
          key: 'images',
          value: 'images',
        },
        {
          label: 'Content',
          key: 'content',
          value: 'content',
        },
      ],
    },
    {
      label: 'Status',
      key: 'status',
      type: 'checkboxes',
      options: [
        {
          label: 'Open',
          key: 'open',
          value: 'open',
        },
        {
          label: 'Resolved',
          key: 'resolved',
          value: 'resolved',
        },
      ],
    },
  ]
  const sortingOptions = [
    { key: 'severity', label: 'Severity', direction: 'desc' },
    { key: 'description', label: 'Description', direction: 'desc' },
    { key: 'asset', label: 'Asset', direction: 'desc' },
    { key: 'created_at', label: 'Created At', direction: 'desc' },
    { key: 'created_by', label: 'Created By', direction: 'desc' },
  ]
  const { searching, sorting, filtering, pagination, queryString } = useTableFilters({
    queryKey,
    filteringOptions,
    sortingOptions,
    enableSearch: true,
    queryExists: true,
    persistKey: `issues-table-${cycleID}`,
  })

  const { data, isLoading, isError, error: errors, refetch } = useQuery([queryKey, queryString])
  const issues = data?.data || []

  const goToAsset = (assetID, assetRouteValue, fieldValueID = null, langCode = null) => {
    const base = `/airlines/${airlineID}/cycles/${cycleID}/metadata/${assetRouteValue}/${assetID}`
    let root
    if (fieldValueID || langCode) {
      const lang =
        langCode || airline?.languages?.find((lang) => lang.id === airline.default_language_id)?.ife_code || 'ENG'
      root = `${base}/metadata?lang=${lang}`
    } else {
      root = `${base}/details`
    }
    if (fieldValueID) {
      root += `#field-errors-${fieldValueID}`
    }
    navigate(root)
  }

  const getSeverityTag = (severity) => {
    let tagType = 'success'
    switch (severity) {
      case 'resolved':
        tagType = 'success'
        break
      case 'warning':
        tagType = 'warning'
        break
      default:
        tagType = 'error'
        break
    }

    return <Badge tone={tagType}>{severity.toUpperCase()}</Badge>
  }

  const resolveCustomIssue = (data) => {
    setIssue(data)
    setIsModalOpen(true)
  }

  const columns = [
    {
      key: 'severity',
      header: 'Severity',
      render: (data) => getSeverityTag(data.deleted_at ? 'resolved' : data.severity),
    },
    {
      key: 'description',
      header: 'Description',
      render: (data) => <div className='overflow-hidden'>{data.description}</div>,
    },
    {
      key: 'asset',
      header: 'Asset',
      render: (data) => (
        <div
          className='cursor-pointer hover:underline'
          onClick={() => goToAsset(data.asset_id, data.asset_route_value)}
        >
          {data?.asset_title}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Created At',
      render: (data) => (data.created_at ? dayjs(data.created_at).format('MMM D, YYYY, h:mm A') : 'N/A'),
    },
    { key: 'created_by', header: 'Created By', render: (data) => data.created_by ?? 'System' },
    { key: 'type', header: 'Type', render: (data) => <Badge>{`${data.type.toUpperCase()}`}</Badge> },
    {
      key: 'actions',
      header: '',
      render: (data) => (
        <Button
          onClick={() =>
            data.asset_id
              ? goToAsset(data.asset_id, data.asset_route_value, data.field_value_id, data.field_value_language)
              : resolveCustomIssue(data)
          }
          variant='secondary'
        >
          {data.deleted_at ? 'View' : 'Resolve'}
        </Button>
      ),
    },
  ]

  return (
    <Page
      title='Issues'
      buttons={[<NewIssue key={'new-issue'} type={'metadata'} cycleID={cycleID} queryKey={queryKey} />]}
    >
      <DataTable
        caption='Issues'
        columns={columns}
        rows={issues ?? []}
        isLoading={isLoading}
        error={errors}
        onRetry={refetch}
        empty={<EmptyState title='No issues found!' detail='We could not find any issues at the moment' />}
        sorting={sorting}
        searching={searching}
        filterControl={
          <>
            <Select
              label='Type'
              hideLabel
              modal={false}
              value={filtering.filters.type?.[0] ?? 'all'}
              onValueChange={(value) => {
                const next = { ...filtering.filters }
                if (!value || value === 'all') delete next.type
                else next.type = [value]
                filtering.onFiltersChange(next)
              }}
              options={[
                { value: 'all', label: 'All types' },
                { value: 'metadata', label: 'Metadata' },
                { value: 'images', label: 'Images' },
                { value: 'content', label: 'Content' },
              ]}
            />
            <Select
              label='Status'
              hideLabel
              modal={false}
              value={filtering.filters.status?.[0] ?? 'all'}
              onValueChange={(value) => {
                const next = { ...filtering.filters }
                if (!value || value === 'all') delete next.status
                else next.status = [value]
                filtering.onFiltersChange(next)
              }}
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'open', label: 'Open' },
                { value: 'resolved', label: 'Resolved' },
              ]}
            />
          </>
        }
        pagination={pagination}
        totalRows={data?.meta.total || 0}
      />
      {isModalOpen && <ResolveIssueModal issue={issue} onClose={() => setIsModalOpen(false)} queryKey={queryKey} />}
    </Page>
  )
}
